import { useState, useRef, useEffect } from "react";
import "../styles/Breathfs.css";
import bfsqueue from '../../../assets/Images/bfsqueue.png'
import bfsvisit from '../../../assets/Images/bfsvisit.png'
import bfslevel from '../../../assets/Images/bfslevel.png'
import bfsshort from '../../../assets/Images/bfsshort.png'

import { visualizeBFS } from "../algorithms/BFSalgo";
import BFSgraph from "./BFSgraph";
import Button from "../../shared/Button";

const BreathFs = () => {
  const [inputValue, setInputValue] = useState("");
  const [graph, setGraph] = useState({});
  const [bfsOrder, setBfsOrder] = useState([]);
  const [traversedNode, setTraversedNode] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [visitedNodes, setVisitedNodes] = useState([]);
  const [queueNodes, setQueueNodes] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Reference to store the pause/resume controller
  const controllerRef = useRef(null);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleAddNode = () => {
    const newGraph = { ...graph };
    const inputParts = inputValue.split(":").map((part) => part.trim());

    if (inputParts.length !== 2) {
      alert('Input must be in the format "Parent: Child1, Child2"');
      return;
    }

    const parentNode = inputParts[0];
    const children = inputParts[1].split(",").map((child) => child.trim());

    if (!newGraph[parentNode]) {
      newGraph[parentNode] = [];
    }
    newGraph[parentNode] = [...newGraph[parentNode], ...children];

    children.forEach((child) => {
      if (!newGraph[child]) {
        newGraph[child] = [];
      }
    });

    setGraph(newGraph);
    setInputValue("");
  };

  const handleBFS = async () => {
    if (isRunning) return;

    setBfsOrder([]);
    setVisitedNodes([]);
    setQueueNodes([]);
    setTraversedNode("");
    setIsRunning(true);
    setIsPaused(false);

    // Create a new controller for this run
    controllerRef.current = {
      isPaused: false,
      pause: () => {
        controllerRef.current.isPaused = true;
        setIsPaused(true);
      },
      resume: () => {
        controllerRef.current.isPaused = false;
        setIsPaused(false);
        controllerRef.current.resolvePromise?.(); // Resolve the waiting promise if it exists
      },
    };

    try {
      const order = await visualizeBFS(
        graph,
        setTraversedNode,
        setVisitedNodes,
        setQueueNodes,
        controllerRef.current
      );
      setBfsOrder(order);
    } finally {
      setIsRunning(false);
      setIsPaused(false);
    }
  };

  const handlePause = () => {
    if (controllerRef.current) {
      controllerRef.current.pause();
    }
  };

  const handleResume = () => {
    if (controllerRef.current) {
      controllerRef.current.resume();
    }
  };

  const handleClear = () => {
    setGraph({});
    setBfsOrder([]);
    setVisitedNodes([]);
    setQueueNodes([]);
    setTraversedNode("");
    setIsRunning(false);
    setIsPaused(false);
    setInputValue("");
  };

  const togglePopup = () => {
    setShowPopup(!showPopup);
  };

  return (
    <div className="Bapp-container">
      <div className="Bsidebar">
        <h1>Graph BFS Visualizer</h1>
        <div className="Binput-container">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Enter nodes (e.g., A: B, C)"
          />
          <Button onClick={handleAddNode} disabled={isRunning}>Add Node</Button>
          <Button onClick={handleBFS} disabled={isRunning}>Run BFS</Button>
          <Button variant="danger" onClick={handleClear}>Clear</Button>
          <Button variant="" onClick={togglePopup}>BFS Conditions</Button>
        </div>

        {isRunning && (
          <div className="Bcontrols-container">
            {isPaused ? (
              <Button onClick={handleResume}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
                Resume
              </Button>
            ) : (
              <Button variant="secondary" onClick={handlePause}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="6" y="4" width="4" height="16"></rect>
                  <rect x="14" y="4" width="4" height="16"></rect>
                </svg>
                Pause
              </Button>
            )}
          </div>
        )}

        <div className="Boutput-container">
          <h2>BFS Order:</h2>
          <div className="Bbfs-order-display">
            {bfsOrder.map((node, index) => (
              <span key={index} className="Bbfs-order-item">
                {node}
              </span>
            ))}
          </div>
        </div>

        <div className="Barray-container">
          <h3>Visited Nodes:</h3>
          <div className="Barray-display">
            {Array.from(visitedNodes).map((node, index) => (
              <span key={index}>{node}</span>
            ))}
          </div>
        </div>

        <div className="Barray-container">
          <h3>Queue:</h3>
          <div className="Barray-display">
            {queueNodes.map((node, index) => (
              <span key={index}>{node}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="Bmain-content">
        <div className="visualization">
          <BFSgraph nodes={graph} traversedNode={traversedNode} visitedNodes={visitedNodes} />
        </div>
      </div>
      <BFSConditionsPopup show={showPopup} onClose={togglePopup} />
    </div>
  );
};

const BFSConditionsPopup = ({ show, onClose }) => {
  if (!show) return null;

  return (
    <div className="Bpopup-overlay">
      <div className="Bpopup-content">
        <div className="Bpopup-close-row">
          <h2>BFS Conditions</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>×</Button>
        </div>
        <div className="conditions">
          <h3>1. Queue Data Structure</h3>
          <p>BFS uses a queue to keep track of nodes to visit next.</p>
          <img src={bfsqueue} alt="Queue illustration" />

          <h3>2. Visited Set</h3>
          <p>A set is used to keep track of visited nodes to avoid cycles.</p>
          <img src={bfsvisit} alt="Visited set illustration" />

          <h3>3. Level Order Traversal</h3>
          <p>
            BFS visits all nodes at the current depth before moving to nodes at
            the next depth level.
          </p>
          <img
            src={bfslevel}
            alt="Level order traversal illustration"
          />

          <h3>4. Shortest Path</h3>
          <p>
            BFS can find the shortest path between two nodes in an unweighted
            graph.
          </p>
          <img src={bfsshort} alt="Shortest path illustration" />
        </div>
      </div>
    </div>
  );
};

export default BreathFs;
